import Big from "big.js";
import { activeDeliveryItems } from "./delivery-items.js";
/**
 * Money maths for offers, settled with the product owner on 2026-08-25 (see
 * docs/handoff/2026-08-25-gold-price-calculation.md for the full story):
 *
 * - big.js with half-up rounding, because the per-karat row amounts are shown to the customer on the
 *   offer they accept, so they must be real two-decimal amounts that add up to the total. Rounding
 *   once at the end would let the rows and the total disagree.
 * - Round only where money exists. Fineness and the internal per-gram offer price are not money;
 *   the per-gram figure is display-only and never quoted to a customer.
 * - The 10 % (payout) / 4 % (gift card) deduction is the business model, so admin sees the raw and
 *   the customer amount side by side while customer surfaces show only the customer amount.
 * - Stone removal is goldsmith work we charge for: the sum of the item charges comes off after the
 *   rate, the same figure for both settlement types, and never takes an offer below zero. A staff
 *   override is the final amount, so the deduction only shapes the computed offer.
 * - Only published price data reaches an offer; nothing in the buying flow derives a price.
 * - Historical assessment documents were not migrated: they were real offers customers accepted.
 */
const NOK_DECIMALS = 2;
const GRAM_DECIMALS = 3;
const KARATS_PER_PURE_GOLD = 24;
export const STANDARD_KARATS = [9, 14, 18, 22, 24];
export function isStandardKarat(value) {
    return STANDARD_KARATS.includes(value);
}
export function karatToFineness(karat) {
    return karat / KARATS_PER_PURE_GOLD;
}
export function karatToPurityPermille(karat) {
    return Math.round(karatToFineness(karat) * 1000);
}
export function karatToPurityPercentage(karat) {
    return Math.round(karatToFineness(karat) * 1000) / 10;
}
export function roundGoldPrice(value) {
    return Number(new Big(value).round(NOK_DECIMALS, Big.roundHalfUp));
}
export function roundWeightGrams(value) {
    return Number(new Big(value).round(GRAM_DECIMALS, Big.roundHalfUp));
}
export const ASSESSMENT_OFFER_RATES = {
    GIFT_CARD: 0.96,
    PAYOUT: 0.9,
};
export const STONE_REMOVAL_PRICE_NOK = 900;
export const PAVE_STONE_REMOVAL_PRICE_NOK = 1500;
export const STONE_REMOVAL_PRICE_BY_CHARGE = {
    STONE_REMOVAL: STONE_REMOVAL_PRICE_NOK,
    PAVE_STONE_REMOVAL: PAVE_STONE_REMOVAL_PRICE_NOK,
};
export function offerPricePerGram(goldPricePerGram, offerRate) {
    const discounted = new Big(goldPricePerGram).times(offerRate);
    return Number(discounted.round(NOK_DECIMALS, Big.roundHalfUp));
}
export function sumNok(values) {
    const total = values.reduce((sum, value) => sum.plus(value), new Big(0));
    return Number(total.round(NOK_DECIMALS, Big.roundHalfUp));
}
/**
 * A row's money: weight × the per-gram offer price rounded to øre, then rounded to øre again. Every
 * figure is two decimals, so the row checks out against the price per gram it was offered at, and a
 * Finago invoice line (quantity × price) comes to the same amount (owner, 2026-10-15).
 */
export function offerValueNok(weightGrams, goldPricePerGram, offerRate) {
    if (weightGrams == null ||
        goldPricePerGram == null ||
        !Number.isFinite(weightGrams) ||
        !Number.isFinite(goldPricePerGram)) {
        return null;
    }
    const value = new Big(weightGrams).times(offerPricePerGram(goldPricePerGram, offerRate));
    return Number(value.round(NOK_DECIMALS, Big.roundHalfUp));
}
export function offerTotalNok(rows, offerRate) {
    const rowValues = rows.map((row) => {
        const goldPricePerGram = row.goldPricePerGram ?? row.pricePerGram;
        return offerValueNok(row.weightGrams, goldPricePerGram, offerRate) ?? 0;
    });
    return sumNok(rowValues);
}
/**
 * Off until the accounting has decided how stone removal is booked (owner, 2026-10-22). While off,
 * no offer or payout is reduced and the deduction lines, shown only above zero, disappear; the
 * charges on items, the kiosk's stone step and its price text stay as they are.
 */
export const STONE_REMOVAL_DEDUCTION_ENABLED = false;
/** What is taken off an offer for the goldsmith work; nothing while the deduction is off. */
export function stoneRemovalDeductionNok(source) {
    return STONE_REMOVAL_DEDUCTION_ENABLED ? stoneRemovalChargesNok(source) : 0;
}
/** What the goldsmith work on a delivery costs: the charge on every item still in it. */
export function stoneRemovalChargesNok(source) {
    return sumNok(activeDeliveryItems(source).flatMap((item) => item.stoneRemovalCharge ? [STONE_REMOVAL_PRICE_BY_CHARGE[item.stoneRemovalCharge]] : []));
}
/** What an offer pays: the rate applied to the rows, less the goldsmith work, never below zero. */
export function netOfferTotalNok(rows, offerRate, deductionNok) {
    return Math.max(0, sumNok([offerTotalNok(rows, offerRate), -deductionNok]));
}
export function deriveNetWeightGrams(grossWeightGrams, stoneWeightGrams) {
    if (grossWeightGrams == null || !Number.isFinite(grossWeightGrams)) {
        return null;
    }
    const stones = stoneWeightGrams ?? 0;
    if (!Number.isFinite(stones))
        return null;
    const net = Number(new Big(grossWeightGrams).minus(stones));
    return roundWeightGrams(Math.max(0, net));
}
export function normalizeKaratPrices(prices) {
    return Object.fromEntries(STANDARD_KARATS.flatMap((karat) => {
        const price = prices[karat];
        return typeof price === "number" && Number.isFinite(price) && price > 0
            ? [[karat, roundGoldPrice(price)]]
            : [];
    }));
}
export function buildAssessmentRows(existingRows, goldPrices) {
    const existingByKarat = new Map(existingRows.map((row) => [row.karat, row]));
    return STANDARD_KARATS.map((karat) => {
        const existing = existingByKarat.get(karat);
        return {
            karat,
            weightGrams: existing?.weightGrams,
            pricePerGram: existing?.goldPricePerGram ?? goldPrices[karat],
        };
    });
}
export function buildOfferOption(rows, offerRate, overrideTotalValue, deductionNok = 0) {
    const nextRows = rows.map((row) => {
        const goldPricePerGram = row.goldPricePerGram ?? row.pricePerGram;
        return {
            ...row,
            goldValue: offerValueNok(row.weightGrams, goldPricePerGram, 1) ?? 0,
            valueNok: offerValueNok(row.weightGrams, goldPricePerGram, offerRate) ?? 0,
        };
    });
    const computedTotalValue = netOfferTotalNok(rows, offerRate, deductionNok);
    return {
        rows: nextRows,
        computedTotalValue,
        totalValue: overrideTotalValue ?? computedTotalValue,
    };
}
export function offerOptionsFromStoredRows({ rows, payoutOverride, giftCardOverride, deductionNok, }) {
    if (!rows || rows.length === 0)
        return null;
    const deduction = deductionNok ?? 0;
    return {
        PAYOUT: payoutOverride ?? netOfferTotalNok(rows, ASSESSMENT_OFFER_RATES.PAYOUT, deduction),
        GIFT_CARD: giftCardOverride ?? netOfferTotalNok(rows, ASSESSMENT_OFFER_RATES.GIFT_CARD, deduction),
    };
}
function pricedDraftRows(rows) {
    return (rows ?? []).flatMap((row) => row.karat != null && row.weightGrams != null && row.weightGrams > 0 && row.pricePerGram != null
        ? [{ karat: row.karat, weightGrams: row.weightGrams, pricePerGram: row.pricePerGram }]
        : []);
}
/**
 * The numbers the assessment screen shows while staff are still editing: the raw gold value, the
 * same value net of stone removal, both settlement options, and the total for the one currently
 * selected.
 */
export function buildAssessmentSummary(rows, settlementType, overrides, deductionNok = 0) {
    const priced = pricedDraftRows(rows);
    const options = {
        PAYOUT: buildOfferOption(priced, ASSESSMENT_OFFER_RATES.PAYOUT, overrides.PAYOUT, deductionNok),
        GIFT_CARD: buildOfferOption(priced, ASSESSMENT_OFFER_RATES.GIFT_CARD, overrides.GIFT_CARD, deductionNok),
    };
    return {
        goldValue: offerTotalNok(priced, 1),
        goldValueAfterDeduction: netOfferTotalNok(priced, 1, deductionNok),
        totalValue: options[settlementType].totalValue,
        options,
    };
}
/**
 * Narrows the form's draft rows to the ones an offer can actually be built from, filling a blank
 * price from today's karat list. A karat may only appear once — two rows for the same karat would
 * each be priced in full, so the customer would be offered more gold than was handed in.
 */
export function assessmentRowsForSubmit(rows, goldPrices) {
    const priced = (rows ?? []).flatMap((row) => {
        if (row.karat == null || row.weightGrams == null || row.weightGrams <= 0)
            return [];
        const pricePerGram = row.pricePerGram ?? goldPrices[row.karat];
        return pricePerGram != null && pricePerGram > 0
            ? [{ karat: row.karat, weightGrams: row.weightGrams, pricePerGram }]
            : [];
    });
    if (priced.length === 0)
        return { ok: false, code: "NO_ROWS" };
    if (new Set(priced.map((row) => row.karat)).size !== priced.length) {
        return { ok: false, code: "DUPLICATE_KARAT" };
    }
    return { ok: true, rows: priced };
}
/**
 * Resets every row's kr/g to today's karat list. A row that has not been given a karat yet takes the
 * one its position implies, which is how the fixed five-row form is laid out.
 */
export function applyGoldPricesToFormRows(rows, goldPrices) {
    return rows.map((row, index) => {
        const karat = row.karat ?? STANDARD_KARATS[index];
        if (karat == null)
            return row;
        return { ...row, karat, pricePerGram: goldPrices[karat] ?? row.pricePerGram };
    });
}
